import { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Play,
  FileText,
  Sparkles,
  ShieldCheck,
  Hash,
  Trash2,
  AlertTriangle,
  ChevronDown,
} from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatBytes, formatDate, cn } from '@/lib/utils';
import { PdfViewer } from '@/components/documents/PdfViewer';
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_LABELS,
  type DocumentType,
} from '@praxara/shared/constants';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

export function DocumentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canDelete = user && ROLE_RANK[user.role] >= ROLE_RANK['ADMIN'];
  const canReclassify = user && ROLE_RANK[user.role] >= ROLE_RANK['REVIEWER'];

  const [showText, setShowText] = useState(false);

  const doc = useQuery({
    queryKey: ['document', id],
    queryFn: async () => (await api.get(`/api/documents/${id}`)).data,
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      return s === 'UPLOADED' || s === 'PROCESSING' ? 2000 : false;
    },
  });

  const workflows = useQuery({
    queryKey: ['workflows'],
    queryFn: async () => (await api.get('/api/workflows')).data,
  });

  const run = useMutation({
    mutationFn: async (workflowId: string) => {
      return api.post('/api/workflows/runs', {
        workflowId,
        documentIds: [id],
      });
    },
    onSuccess: (res) => {
      toast.success('Workflow started', 'Tracking execution');
      qc.invalidateQueries({ queryKey: ['runs'] });
      navigate(`/workflows/runs/${res.data.id}`);
    },
    onError: (err) => {
      toast.error('Failed to start workflow', extractApiError(err).message);
    },
  });

  const del = useMutation({
    mutationFn: async () => api.delete(`/api/documents/${id}`),
    onSuccess: () => {
      toast.success('Document deleted');
      qc.invalidateQueries({ queryKey: ['documents'] });
      navigate('/documents');
    },
    onError: (err) => toast.error('Delete failed', extractApiError(err).message),
  });

  // D-5 — Reject-and-reclassify. Calls PATCH /api/documents/:id/reclassify
  // (existing endpoint). Optimistic update: set documentType in cache so the
  // type badge flips immediately; rollback on error.
  const reclassify = useMutation({
    mutationFn: async (newType: DocumentType) =>
      api.patch(`/api/documents/${id}/reclassify`, { documentType: newType }),
    onMutate: async (newType: DocumentType) => {
      await qc.cancelQueries({ queryKey: ['document', id] });
      const previous = qc.getQueryData(['document', id]);
      qc.setQueryData(['document', id], (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        return { ...(old as Record<string, unknown>), documentType: newType };
      });
      return { previous };
    },
    onError: (err, _newType, ctx) => {
      if (ctx?.previous !== undefined) qc.setQueryData(['document', id], ctx.previous);
      toast.error('Reclassify failed', extractApiError(err).message);
    },
    onSuccess: (_res, newType) => {
      toast.success(
        'Document reclassified',
        `Now classified as ${DOCUMENT_TYPE_LABELS[newType]}.`,
      );
      qc.invalidateQueries({ queryKey: ['document', id] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  if (doc.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!doc.data) return <div className="text-sm">Not found</div>;

  const d = doc.data;
  const ready = d.status === 'TEXT_EXTRACTED' || d.status === 'PROCESSED';
  const failed = d.status === 'FAILED';
  const isPdf = d.mimeType === 'application/pdf';
  const typeLabel = d.documentType ? DOCUMENT_TYPE_LABELS[d.documentType as DocumentType] : 'Unclassified';
  const meta = d.metadata ?? {};

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
            <Link to="/documents">
              <ArrowLeft className="mr-2 h-4 w-4" /> Documents
            </Link>
          </Button>
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="truncate font-display text-2xl">{d.fileName}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <ReclassifyMenu
                  currentType={d.documentType ?? null}
                  disabled={!canReclassify || reclassify.isPending}
                  onSelect={(t) => reclassify.mutate(t)}
                />
                <span>·</span>
                <span>{formatBytes(d.fileSizeBytes)}</span>
                <span>·</span>
                <span>{d.pageCount ?? '—'} pages</span>
                <span>·</span>
                <span className="uppercase tracking-wider">{d.fileType}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={d.status} />
          {canDelete && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm('Delete this document? This cannot be undone.')) del.mutate();
              }}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      </div>

      {failed && d.failureReason && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
            <div className="text-sm">
              <div className="font-medium text-destructive">Text extraction failed</div>
              <div className="text-muted-foreground">{d.failureReason}</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Split: viewer + info sidebar */}
      <div className="grid gap-4 lg:grid-cols-[1fr,380px]">
        {/* Preview */}
        <div className="h-[calc(100vh-13rem)]">
          {isPdf ? (
            <PdfViewer documentId={d.id} fileName={d.fileName} className="h-full" />
          ) : (
            <Card className="flex h-full flex-col">
              <CardHeader>
                <CardTitle>Preview</CardTitle>
                <CardDescription>Inline preview available for PDFs.</CardDescription>
              </CardHeader>
              <CardContent className="flex-1 overflow-auto p-4 text-sm text-muted-foreground">
                {d.extractedText ? (
                  <pre className="whitespace-pre-wrap break-words font-mono text-xs">
                    {d.extractedText.slice(0, 20000)}
                    {d.extractedText.length > 20000 && '\n\n[...truncated]'}
                  </pre>
                ) : (
                  'No preview available.'
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Metadata */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Document details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Type" value={typeLabel} />
              <Row label="Category" value={d.category ?? '—'} />
              <Row label="MIME" value={<code className="text-xs">{d.mimeType}</code>} />
              <Row label="Pages" value={d.pageCount ?? '—'} />
              <Row label="Size" value={formatBytes(d.fileSizeBytes)} />
              <Row
                label="Checksum"
                value={
                  <span className="flex items-center gap-1">
                    <Hash className="h-3 w-3 text-muted-foreground" />
                    <code className="text-xs">{d.checksumSha256.slice(0, 12)}...</code>
                  </span>
                }
              />
              <Row label="Uploaded" value={formatDate(d.createdAt)} />
            </CardContent>
          </Card>

          {/* OCR */}
          {(meta.ocrSource || meta.source) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-primary" /> OCR + extraction quality
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(() => {
                  // D-4 — combined OCR engine + confidence label, with special
                  // handling for spreadsheet-reference (no confidence — it's a
                  // template, not OCR output).
                  const ocrSource: string = meta.ocrSource ?? meta.source ?? '';
                  const conf: number | undefined =
                    typeof meta.ocrConfidence === 'number' ? meta.ocrConfidence : undefined;
                  const isSpreadsheetRef = ocrSource === 'spreadsheet-reference';
                  return (
                    <div className="rounded-md border bg-muted/20 p-2 text-xs">
                      {isSpreadsheetRef ? (
                        <span>
                          <span className="font-semibold">OCR engine:</span>{' '}
                          {ocrLabel(ocrSource)} · Reference template — view in source application
                        </span>
                      ) : (
                        <span>
                          <span className="font-semibold">OCR engine:</span>{' '}
                          {ocrLabel(ocrSource)}
                          {typeof conf === 'number' && (
                            <>
                              {' · '}
                              <span className="font-semibold">Confidence:</span>{' '}
                              <span
                                className={
                                  conf >= 0.9
                                    ? 'text-emerald-600'
                                    : conf >= 0.7
                                      ? 'text-amber-600'
                                      : 'text-destructive'
                                }
                              >
                                {Math.round(conf * 100)}%
                              </span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })()}
                {Array.isArray(meta.tables) && (
                  <Row label="Tables detected" value={meta.tables.length} />
                )}
                {Array.isArray(meta.sections) && (
                  <Row label="Sections detected" value={meta.sections.length} />
                )}
                {Array.isArray(meta.warnings) && meta.warnings.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                    <div className="font-semibold">{meta.warnings.length} warning{meta.warnings.length > 1 ? 's' : ''}</div>
                    <ul className="mt-1 space-y-0.5">
                      {meta.warnings.slice(0, 3).map((w: string, i: number) => (
                        <li key={i} className="truncate">· {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Workflows */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Play className="h-4 w-4 text-primary" /> Run a workflow
              </CardTitle>
              {!ready && (
                <CardDescription>Text extraction must complete first.</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {ready && workflows.data?.items?.length === 0 && (
                <div className="text-xs text-muted-foreground">No workflows defined yet.</div>
              )}
              {ready &&
                (() => {
                  // Skill router: rank workflows by compatibility with this
                  // document's classified type. Recommended = workflow lists
                  // this documentType in definition.applicableDocumentTypes.
                  // Incompatible = workflow has a list and we're not in it
                  // (clicking will produce a 422 from the server-side guard,
                  // so we visually disable instead of letting the user fail).
                  type WorkflowRow = {
                    id: string;
                    name: string;
                    description: string | null;
                    type: string;
                    definition?: { applicableDocumentTypes?: string[] };
                  };
                  const items = (workflows.data?.items ?? []) as WorkflowRow[];
                  const docType = d.documentType;
                  const ranked = items
                    .map((w) => {
                      const allowed = w.definition?.applicableDocumentTypes;
                      let rank: 'recommended' | 'compatible' | 'incompatible' = 'compatible';
                      if (allowed && allowed.length > 0) {
                        rank = docType && allowed.includes(docType) ? 'recommended' : 'incompatible';
                      }
                      return { w, rank };
                    })
                    .sort((a, b) => {
                      const order = { recommended: 0, compatible: 1, incompatible: 2 };
                      return order[a.rank] - order[b.rank];
                    });
                  return ranked.map(({ w, rank }) => (
                    <button
                      key={w.id}
                      onClick={() => run.mutate(w.id)}
                      disabled={run.isPending || rank === 'incompatible'}
                      title={rank === 'incompatible' ? `This workflow is not applicable to documentType=${docType ?? 'unclassified'}` : undefined}
                      className={cn(
                        'flex w-full items-start justify-between gap-3 rounded-md border p-3 text-left transition-colors',
                        rank === 'recommended' && 'border-primary/60 bg-primary/5',
                        rank === 'incompatible' && 'opacity-40',
                        'hover:border-primary/40 hover:bg-accent/30 disabled:opacity-40',
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          {w.name}
                          {rank === 'recommended' && (
                            <span className="rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                              Recommended
                            </span>
                          )}
                          {rank === 'incompatible' && (
                            <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                              Not applicable
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                          {w.description}
                        </div>
                      </div>
                      <Play className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </button>
                  ));
                })()}
            </CardContent>
          </Card>

          {/* 21 CFR Part 11 hint */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="flex items-start gap-3 p-4">
              <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" />
              <div className="text-xs text-muted-foreground">
                All document actions are audited with SHA-256-chained integrity. Extraction output
                requires reviewer approval with e-signature before it can be exported.
              </div>
            </CardContent>
          </Card>

          {/* Extracted text toggle */}
          {d.extractedText && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm">Extracted text</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowText((s) => !s)}>
                  {showText ? 'Hide' : 'Show'}
                </Button>
              </CardHeader>
              {showText && (
                <CardContent>
                  <pre className="scrollbar-thin max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-3 text-xs">
                    {d.extractedText.slice(0, 20_000)}
                    {d.extractedText.length > 20_000 && '\n\n[...truncated]'}
                  </pre>
                </CardContent>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b py-1.5 last:border-0 last:pb-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-sm font-medium">{value}</span>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive'> = {
    UPLOADED: 'secondary',
    PROCESSING: 'info',
    TEXT_EXTRACTED: 'success',
    PROCESSED: 'success',
    FAILED: 'destructive',
  };
  const label: Record<string, string> = {
    UPLOADED: 'Queued',
    PROCESSING: 'Processing',
    TEXT_EXTRACTED: 'Extracted',
    PROCESSED: 'Processed',
    FAILED: 'Failed',
  };
  return <Badge variant={v[status] ?? 'secondary'}>{label[status] ?? status}</Badge>;
}

function ocrLabel(source: string): string {
  switch (source) {
    case 'azure-di':
      return 'Azure Document Intelligence';
    case 'pdf-parse':
    case 'pdf-parse-fallback':
      return 'pdf-parse (fallback)';
    case 'mammoth':
      return 'Mammoth';
    case 'txt':
      return 'Plain text';
    case 'spreadsheet-reference':
      return 'Spreadsheet reference';
    default:
      return source || 'unknown';
  }
}

/**
 * D-5 — Reject-and-reclassify. Type badge that opens a dropdown of every
 * DocumentType enum value when clicked. Selecting one fires the parent's
 * onSelect callback (PATCH /api/documents/:id/reclassify). Read-only when
 * disabled (non-reviewer or in-flight mutation).
 */
function ReclassifyMenu({
  currentType,
  disabled,
  onSelect,
}: {
  currentType: string | null;
  disabled: boolean;
  onSelect: (newType: DocumentType) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const label = currentType
    ? DOCUMENT_TYPE_LABELS[currentType as DocumentType] ?? 'Unclassified'
    : 'Unclassified';

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((s) => !s)}
        onContextMenu={(e) => {
          if (disabled) return;
          e.preventDefault();
          setOpen(true);
        }}
        title={disabled ? undefined : 'Click or right-click to reclassify'}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border bg-background px-2 py-0.5 text-xs font-medium',
          'border-primary/30 text-foreground',
          !disabled && 'cursor-pointer hover:border-primary/60 hover:bg-primary/5',
          disabled && 'cursor-default opacity-80',
        )}
      >
        <span>{label}</span>
        {!disabled && <ChevronDown className="h-3 w-3" />}
      </button>
      {open && !disabled && (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-1 max-h-72 w-72 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <div className="border-b px-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            Reclassify document
          </div>
          {DOCUMENT_TYPES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setOpen(false);
                if (t !== currentType) onSelect(t);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-xs hover:bg-accent',
                t === currentType && 'bg-primary/10 text-primary',
              )}
            >
              <span className="truncate">{DOCUMENT_TYPE_LABELS[t]}</span>
              <span className="ml-2 shrink-0 font-mono text-[10px] text-muted-foreground">
                {t}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
